// Country-driven date/time formatting for the panelist app.
//
// API responses ship raw UTC ISO timestamps; this utility:
//   - converts them to the active country's timezone (countryConfig.timezone)
//   - renders them in the locale derived from the user's selected language
//     (state.adminUser.adminUser.language → LANGUAGE_LOCALE map)
// Timezone is country-driven; locale is language-driven. They must stay
// independent — a JP user reading the panel in English still expects JST
// timestamps but formatted in en-US, not ja-JP.
//
// It uses the native Intl.DateTimeFormat — no extra library — because `moment`
// (already in the project) cannot convert to an arbitrary IANA timezone
// without the separate moment-timezone add-on, which is not installed.
//
// For ja-JP we hand-build the kanji form (2026年5月19日) because
// Intl.DateTimeFormat with 'ja-JP' produces the slash form (2026/5/19);
// the kanji form reads as most clearly Japanese. All other locales use the
// Intl default for that locale.

import countryConfig from '../config/countryConfig';
import { store } from '../reducers';

const LANGUAGE_LOCALE: Record<string, string> = {
  en: 'en-US',
  jp: 'ja-JP',
  hi: 'hi-IN',
};

function readActiveLocale(): string {
  try {
    const lang = store.getState()?.adminUser?.adminUser?.language;
    if (lang && LANGUAGE_LOCALE[lang]) return LANGUAGE_LOCALE[lang];
  } catch {
    // store not ready (e.g. very early boot) — fall through to countryConfig.
  }
  // Fallback when language is absent: countryConfig.locale, then en-US.
  // countryConfig.locale is no longer the authoritative source for format —
  // it's a last-resort fallback only.
  return countryConfig.locale ?? 'en-US';
}

export type DateVariant = 'date' | 'time' | 'datetime';

// Build the parts for a value in the country's timezone. Returns null when
// the input cannot be parsed (avoids "Invalid Date" / NaN reaching the UI).
function partsInTimezone(
  value: string | Date,
  timeZone: string,
): Record<string, string> | null {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== 'literal') parts[p.type] = p.value;
  }
  // Intl can emit hour '24' at midnight for hour12:false — normalize to '00'.
  if (parts.hour === '24') parts.hour = '00';
  return parts;
}

/**
 * Format a UTC date/time value for the active country.
 *
 * @param value   ISO string or Date (treated as UTC when an ISO string).
 * @param variant 'date' | 'time' | 'datetime' (default 'datetime').
 * @returns       Localized string, or '' for an unparseable value.
 */
export function formatDate(
  value: string | Date | null | undefined,
  variant: DateVariant = 'datetime',
): string {
  if (value === null || value === undefined || value === '') return '';

  const locale = readActiveLocale();
  const timeZone = countryConfig.timezone ?? 'Asia/Kolkata';

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  // Japan: hand-built kanji format in JST.
  if (locale === 'ja-JP') {
    const p = partsInTimezone(d, timeZone);
    if (!p) return '';
    // Strip leading zeros on month/day for natural Japanese rendering.
    const y = p.year;
    const mo = String(parseInt(p.month, 10));
    const day = String(parseInt(p.day, 10));
    const datePart = `${y}年${mo}月${day}日`;
    const timePart = `${p.hour}:${p.minute}`;
    if (variant === 'date') return datePart;
    if (variant === 'time') return timePart;
    return `${datePart} ${timePart}`;
  }

  // All other locales: Use dd-mm-yyyy format, converted to the
  // country's timezone. 24-hour for consistency with the JP output.
  const p = partsInTimezone(d, timeZone);
  if (!p) return '';
  const datePart = `${p.day}-${p.month}-${p.year}`;
  const timePart = `${p.hour}:${p.minute}`;

  if (variant === 'date') return datePart;
  if (variant === 'time') return timePart;
  return `${datePart} ${timePart}`;
}

export default formatDate;
